import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Card } from '../Card';
import { Tags } from './Tags';

const StyledCard = styled(Card)`
  height: 100%;
  min-width: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
  transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  transform-style: preserve-3d;
  border: 1px solid ${props => props.theme.colors.border.light};
  border-radius: 24px !important;
  background-color: #FFFFFF !important;

  [data-theme="dark"] & {
    background-color: ${props => props.theme.colors.background.secondary} !important;
  }

  &:hover {
    box-shadow: ${props => props.theme.shadows.lg};
    border-color: #6B8E23;
  }
`;

const ContentImageContainer = styled.div`
  width: 100%;
  height: 200px;
  border-radius: 24px 24px 0 0;
  overflow: hidden;
  position: relative;
`;

const ContentImage = styled.div`
  width: 100%;
  height: 100%;
  background-color: ${props => props.theme.colors.background.tertiary};
  background-image: url(${props => props.image});
  background-size: 120%;
  background-position: center;
  transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const ContentBody = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ContentTitle = styled.h3`
  margin-bottom: 0.75rem;
  font-size: ${props => props.theme.typography.h5};
  line-height: 1.3;
  color: ${props => props.theme.colors.text.primary};
`;

const ContentDescription = styled.p`
  flex: 1;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.text.secondary};
  line-height: 1.6;
`;

const ContentMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.text.tertiary};
  font-size: ${props => props.theme.typography.small};

  .meta-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;


const ContentActions = styled.div`
  display: flex;
  gap: 1rem;
`;

const YearPill = styled.span`
  position: absolute;
  bottom: 0.625rem;
  right: 0.625rem;
  background: rgba(255, 255, 255, 0.92);
  color: #3a5a10;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  letter-spacing: 0.02em;
  backdrop-filter: blur(4px);
`;

export const ContentCard = ({
  item,
  variant = 'article', // 'article' or 'project'
  linkTo,
  featuredImage,
  title,
  description,
  year,
  metaItems = [],
  tags = [],
  actions = null,
  children
}) => {
  const cardRef = useRef(null);
  const imageRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);

  const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;

  const handleMouseMove = (e) => {
    if (!cardRef.current || !imageRef.current) return;

    const card = cardRef.current;
    const image = imageRef.current;
    const rect = card.getBoundingClientRect();

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = (y - centerY) / 10;
    const rotateY = (centerX - x) / 10;

    // Parallax effect for image
    const moveX = (x - centerX) / 20;
    const moveY = (y - centerY) / 20;

    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateZ(10px)`;
    image.style.transform = `translate(${moveX}px, ${moveY}px) scale(1.1)`;
  };

  const handleMouseEnter = () => {
    setIsHovering(true);
    if (cardRef.current && imageRef.current) {
      cardRef.current.style.transition = '';
      imageRef.current.style.transition = '';
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    if (cardRef.current && imageRef.current) {
      cardRef.current.style.transition = 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      imageRef.current.style.transition = 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
      requestAnimationFrame(() => {
        if (cardRef.current && imageRef.current) {
          cardRef.current.style.transform = '';
          imageRef.current.style.transform = '';
        }
      });
    }
  };

  const CardComponent = linkTo ?
    (props) => <StyledCard as={Link} to={linkTo} hoverable {...props} /> :
    (props) => <StyledCard hoverable {...props} />;

  return (
    <CardComponent
      ref={cardRef}
      onMouseMove={isTouchDevice ? undefined : handleMouseMove}
      onMouseEnter={isTouchDevice ? undefined : handleMouseEnter}
      onMouseLeave={isTouchDevice ? undefined : handleMouseLeave}
    >
      {featuredImage && (
        <ContentImageContainer>
          <ContentImage
            ref={imageRef}
            image={featuredImage}
          />
          {year && <YearPill>{year}</YearPill>}
        </ContentImageContainer>
      )}

      <ContentBody>
        <ContentTitle data-cursor-text>{title}</ContentTitle>
        <ContentDescription data-cursor-text>{description}</ContentDescription>

        {metaItems.length > 0 && (
          <ContentMeta>
            {metaItems.map((meta, index) => (
              <div key={index} className="meta-item" data-cursor-text>
                {meta.icon}
                {meta.text}
              </div>
            ))}
          </ContentMeta>
        )}

        <Tags tags={tags} variant={variant} />

        {actions && (
          <ContentActions>
            {actions}
          </ContentActions>
        )}

        {children}
      </ContentBody>
    </CardComponent>
  );
};